import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { combineLatest, Observable, Subscription } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { IDsBreadcrumbItem } from '@levelaccess/design-system';

import { Api } from '../../../../../../shared/constants/api';
import { ITenant } from '../../../../../../shared/interfaces/tenant.interface';
import { IWorkspace } from '../../../../../../shared/interfaces/workspace.interface';
import { TenantService } from '../../../../services/tenant.service';
import { WorkspaceService } from '../../../../services/workspace.service';
import { TranslateService } from '../../../../translate/translate.service';
import { $tenant } from '../../../../../../shared/constants/tenant';
import { $workspace } from '../../../../../../shared/constants/workspace';
import { RoutingService } from '../../../../services/routing.service';
import { TenantRouteUtil } from '../../tenant-route.util';
import { ErrorHandlerService } from '../../../../services/error-handler.service';

@Component({
  selector: 'app-create-digital-property',
  templateUrl: './create-digital-property.component.html',
  standalone: false,
})
export class CreateDigitalPropertyComponent implements OnInit, OnDestroy {
  private subscriptions: Subscription = new Subscription();
  public readonly tenantRoutePath: string;

  public tenantId: string;
  public workspaceId: string;
  public breadcrumbs$: Observable<IDsBreadcrumbItem[]>;

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private tenantService: TenantService,
    private workspaceService: WorkspaceService,
    private translateService: TranslateService,
    private routingService: RoutingService,
    private errorHandlerService: ErrorHandlerService,
  ) {
    this.tenantRoutePath = TenantRouteUtil.getTenantRoutePath(this.router.url);
  }

  public ngOnInit(): void {
    this.subscriptions.add(
      this.activatedRoute.paramMap.subscribe((params: ParamMap) => {
        this.workspaceId = params.get(Api.workspaceId);
        this.tenantId = params.get(Api.tenantId);
      }),
    );

    const tenant$: Observable<ITenant> = this.activatedRoute.paramMap.pipe(
      map((params: ParamMap) => params.get(Api.tenantId)),
      switchMap((tenantId: string) => this.tenantService.getTenant(tenantId)),
      catchError(this.errorHandlerService.handleNotFound()),
    );

    const workspace$: Observable<IWorkspace> = this.activatedRoute.paramMap.pipe(
      map((params: ParamMap) => params.get(Api.workspaceId)),
      switchMap((workspaceId: string) => this.workspaceService.getWorkspace(workspaceId)),
      catchError(this.errorHandlerService.handleNotFound()),
    );

    this.breadcrumbs$ = combineLatest([tenant$, workspace$]).pipe(
      map(([tenant, workspace]: [ITenant, IWorkspace]) => {
        return [
          {
            label: this.translateService.instant('tenants'),
            rtLink: `${this.tenantRoutePath}`,
          },
          {
            label: tenant[$tenant.name],
            rtLink: `${this.tenantRoutePath}/${tenant[$tenant._id]}`,
          },
          {
            label: workspace[$workspace.name],
            rtLink: `${this.tenantRoutePath}/${tenant[$tenant._id]}/${Api.workspaces}/${workspace[$workspace._id]}`,
          },
          {
            label: this.translateService.instant(this.routingService.getPageTitle()),
            rtLink: '.',
          },
        ];
      }),
    );
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
